// Правая лента карточки — как таймлайн Bitrix: композер комментария сверху,
// ниже события по дням («Сегодня» / «Вчера» / дата), свежие первыми.
//
// Компонент показывает ТО, ЧТО ЕМУ ДАЛИ, и не ходит за данными сам: события приносит
// страница (сейчас — из существующих ручек и заглушки api/cardLive.ts). Событие из
// зеркала Bitrix помечается капсулой origin — чтобы чужая запись не читалась как наша.
//
// Отправка комментария отдаётся наружу и ЧЕСТНО возвращает ответ: пока ручки ядра нет,
// заглушка отвечает {ok: false, почему}, и лента показывает причину, а не делает вид,
// что комментарий сохранён.
import { useState } from 'react'
import type { ReactNode } from 'react'

import { daysPast, fmtDate } from '../../lib/format'
import './card.css'

export interface TimelineEvent {
  id: string | number
  /** ISO-время события. Пустое — событие уходит в конец, под «Без даты». */
  at?: string | null
  /** comment | stage | field | bitrix — подпись вида в строке события. */
  kind: string
  author?: string
  text: ReactNode
  origin?: 'crm' | 'bitrix'
}

export interface CommentResult { ok: boolean; почему?: string }

interface Props {
  events: TimelineEvent[]
  /** Нет обработчика — композер не рисуется (нет права записи). */
  onComment?: (text: string) => Promise<CommentResult>
  /** Оговорка о границах данных ленты (например, «ручка ядра не подключена»). */
  note?: string
  /** Подгрузка следующей страницы событий. */
  more?: { has: boolean; loading?: boolean; onMore: () => void }
  /** Что сказать словами, когда событий нет. */
  emptyText?: string
}

const ВИДЫ: Record<string, string> = {
  comment: 'комментарий',
  stage: 'стадия',
  field: 'поле',
  bitrix: 'Bitrix',
}

/** «Сегодня» / «Вчера» / дата — по календарным дням, а не по 24 часам. */
function деньСловами(day: string): string {
  const прошло = daysPast(day)
  if (прошло === 0) return 'Сегодня'
  if (прошло === 1) return 'Вчера'
  return fmtDate(day)
}

export function Timeline({ events, onComment, note, more, emptyText }: Props) {
  const [текст, setТекст] = useState('')
  const [шлём, setШлём] = useState(false)
  const [ответ, setОтвет] = useState<CommentResult | null>(null)

  const отправить = async () => {
    if (!onComment) return
    const т = текст.trim()
    if (!т) return
    setШлём(true)
    setОтвет(null)
    try {
      const р = await onComment(т)
      setОтвет(р)
      if (р.ok) setТекст('')
    } catch (e) {
      setОтвет({ ok: false, почему: e instanceof Error ? e.message : String(e) })
    } finally {
      setШлём(false)
    }
  }

  // События свежими вперёд; без даты — в конец. Заголовок дня вставляется на смене дня.
  const поПорядку = [...events].sort((a, b) => {
    const ад = a.at ?? ''
    const бд = b.at ?? ''
    if (!ад !== !бд) return ад ? -1 : 1
    return бд.localeCompare(ад)
  })

  let прошлыйДень = ''
  return (
    <div className="timeline">
      {onComment && (
        <div className="tl-composer">
          <textarea
            value={текст}
            placeholder="Комментарий к карточке…"
            aria-label="Комментарий"
            onChange={(e) => setТекст(e.target.value)}
          />
          <div className="tl-actions">
            <button type="button" className="primary" disabled={шлём || !текст.trim()}
                    onClick={() => void отправить()}>
              {шлём ? 'Отправляю…' : 'Отправить'}
            </button>
            {ответ && !ответ.ok && (
              <span className="error">{ответ.почему || 'комментарий не сохранён'}</span>
            )}
          </div>
        </div>
      )}

      {поПорядку.length === 0 ? (
        <div className="empty">
          <div className="empty-title">{emptyText ?? 'Событий в ленте нет'}</div>
          {note && <div className="empty-hint">{note}</div>}
        </div>
      ) : (
        <>
          {поПорядку.map((с) => {
            const день = с.at ? String(с.at).slice(0, 10) : ''
            const заголовок = день !== прошлыйДень
              ? <div className="tl-day">{день ? деньСловами(день) : 'Без даты'}</div>
              : null
            прошлыйДень = день
            return (
              <div key={с.id}>
                {заголовок}
                <div className="tl-item">
                  <div className="tl-meta">
                    <span>{ВИДЫ[с.kind] ?? с.kind}</span>
                    {с.at && <span>{fmtDate(с.at, true)}</span>}
                    {с.author && <span>{с.author}</span>}
                    {с.origin === 'bitrix' && (
                      <span className="origin-mark"
                            title="Событие зеркала Bitrix, а не записи CRM">
                        Bitrix
                      </span>
                    )}
                  </div>
                  <div className="tl-text">{с.text}</div>
                </div>
              </div>
            )
          })}
          {more?.has && (
            <button type="button" onClick={more.onMore} disabled={more.loading}>
              {more.loading ? 'Загружаю…' : 'Показать ещё'}
            </button>
          )}
          {note && <div className="hint">{note}</div>}
        </>
      )}
    </div>
  )
}
